import { Serializable } from "@fableous/core/util/Serialization";
import { SocketMessage } from "@fableous/core/controller/ApiMessageTypes";

import { useState, useEffect, useRef, ChangeEventHandler, ChangeEvent } from "react";
import { ActionVerb } from "@fableous/core/model/Enums";

//https://stackoverflow.com/questions/53451584/is-it-possible-to-share-states-between-components-using-the-usestate-hook-in-r

export function makeObservable<T>(target: T) {
	type StandardListener<T> = (val: T) => void;

	let listeners: StandardListener<T>[] = [];
	let value = target;

	function get(): T {
		return value;
	}

	function set(newValue: T): void {
		if (value === newValue) return;
		value = newValue;
		listeners.forEach((l) => l(value));
	}

	function subscribe(listenerFunc: StandardListener<T>) {
		listeners.push(listenerFunc);
		return () => unsubscribe(listenerFunc); // will be used inside React.useEffect
	}

	function unsubscribe(listenerFunc: StandardListener<T>) {
		listeners = listeners.filter((l) => l !== listenerFunc);
	}

	return {
		get,
		set,
		subscribe,
	};
}

export type SyncAction<State> = {
	type: "__sync__";
	payload: State;
};

export type ObservableReducerStore<State, Action> = {
	get: () => State;
	set: (state: State) => void;
	dispatch: (action: Action | SyncAction<State>) => void;
	subscribe: (listener: (state: State) => void) => () => void;
	reducer: (state: State, action: Action | SyncAction<State>) => State;
	initialState: State;
};

export function makeObservableReducer<State, Action extends { type: string }>(
	reducer: (state: State, action: Action | SyncAction<State>) => State,
	initialState: State
): ObservableReducerStore<State, Action> {
	let listeners: ((state: State) => void)[] = [];
	let state = initialState;

	function get(): State {
		return state;
	}

	function set(newState: State) {
		if (state === newState) return;
		state = newState;
		listeners.forEach((listener) => listener(state));
	}

	function dispatch(action: Action | SyncAction<State>) {
		const newState = reducer(state, action);
		if (newState !== state) {
			set(newState);
		}
	}

	function subscribe(listener: (state: State) => void): () => void {
		listeners.push(listener);
		return () => {
			listeners = listeners.filter((l) => l !== listener);
		};
	}

	return {
		get,
		set,
		dispatch,
		subscribe,
		reducer,
		initialState,
	};
}

export type SocketListener<T extends Serializable> = {
	onMessage: (message: SocketMessage<T>) => void;
	onOpen?: () => void;
	onClose?: (code: number, reason: string) => void;
	onError: (error: SocketMessage<T> | Event) => void;
	onPing?: (data: Buffer) => void;
	onPong?: (data: Buffer) => void;
	actionVerb?: ActionVerb | ActionVerb[];
};

export type SocketMessageOptions = {
	userRegion?: string;
	userId?: string;
	pathParameters?: object; //For passing onto lambda function (like a GET request)
	timeout?: number; //Timeout to apply for each call (resets whenever any data is received)
	completeOnResponse?: boolean; //Should the call complete on the first "response", or wait for finish message
	startingProgress?: number; //The min the call should contribute to the progress bar, defaults to 0
	endingProgress?: number; //The max the call should contribute to the progress bar, defaults to 100
	expectedMessageQuantity?: number;
};

export type SocketMessageProps<T> = {
	actionVerb: ActionVerb;
	actionVerbsToListen? : ActionVerb[];
	data?: T;
	options?: SocketMessageOptions;
};

type FieldsType = {
	[key: string | symbol]: string;
};

export async function delay(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function timeout(time : number) : Promise<never> {
  return new Promise((_, reject) =>
	setTimeout(() => reject(new Error("Operation timed out")), time)
  );
}

export function useFormFields(initialState: FieldsType): [FieldsType, ChangeEventHandler] {
	const [fields, setValues] = useState(initialState);

	return [
		fields,
		function (event: ChangeEvent<HTMLInputElement>) {
			setValues({
				...fields,
				[event.target.id]: event.target.value,
			});
			return;
		},
	];
}

export function useInterval(callback: () => void, delay: number | undefined, id: string = "") {
	const savedCallback = useRef<() => void>();
	const [count, setCount] = useState(0);

	useEffect(() => {
		savedCallback.current = callback;
	}, [callback]);

	useEffect(() => {
		function tick() {
			if (savedCallback.current) savedCallback.current();
			setCount((prevCount) => prevCount + 1);
		}
		if (delay !== undefined) {
			const id = setInterval(tick, delay);
			return () => {
				clearInterval(id);
			};
		}
	}, [delay]);

	return {
		["count" + id]: count,
	};
}

export function useScript(url: string, condition = true) {
	useEffect(() => {
		let script: HTMLScriptElement;
		if (condition) {
			script = document.createElement("script");
			script.src = url;
			script.async = true;
			document.head.appendChild(script);
		}

		return () => {
			if (script) document.head.removeChild(script);
		};
	}, [url, condition]);
}

export function usePageTitle(title: string) {
	useEffect(() => {
		document.title = title && title !== "title" ? title + " | Pitchli" : "Pitchli";
	}, [title]);
}
